import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { appVersion, lastBuildDate } from 'src/consumer/services'
import { getApiDebugQueryConfig, resolveApiDomainConfig } from 'src/consumer/services/apiDomainConfig'

function getDisplayHost(url) {
  if (!url) {
    return 'not-set'
  }

  try {
    const parsed = new URL(url)
    return `${parsed.host}${parsed.pathname === '/' ? '' : parsed.pathname}`
  } catch {
    return url
  }
}

function shouldDefaultToCompactMode(pathname = '') {
  return (
    pathname.startsWith('/kiosk/') ||
    pathname.startsWith('/l/') ||
    pathname.startsWith('/v/')
  )
}

export default function ApiDebugStatusBadge({ debugMode = false, onExitDebugMode }) {
  const location = useLocation()
  const apiDomainConfig = resolveApiDomainConfig()
  const debugQueryConfig = getApiDebugQueryConfig()
  const badgeRef = useRef(null)
  const dragStateRef = useRef(null)
  const suppressToggleRef = useRef(false)
  const [userToggled, setUserToggled] = useState(false)
  const [dragPosition, setDragPosition] = useState(null)
  const hasStoredApiDeviation =
    Boolean(apiDomainConfig.storedDefaultApiBaseUrl) &&
    apiDomainConfig.storedDefaultApiBaseUrl !== apiDomainConfig.defaultApiBaseUrl
  const hasStoredPublicApiDeviation =
    Boolean(apiDomainConfig.storedPublicApiBaseUrl) &&
    apiDomainConfig.storedPublicApiBaseUrl !== apiDomainConfig.defaultPublicApiBaseUrl

  const shouldShow =
    debugMode ||
    debugQueryConfig.debugPanelRequested ||
    Boolean(apiDomainConfig.customApiBaseUrl) ||
    Boolean(apiDomainConfig.customPublicApiBaseUrl) ||
    hasStoredApiDeviation ||
    hasStoredPublicApiDeviation

  const defaultCompactMode = useMemo(
    () => shouldDefaultToCompactMode(location.pathname),
    [location.pathname],
  )
  const [minimized, setMinimized] = useState(defaultCompactMode)

  useEffect(() => {
    if (!userToggled) {
      setMinimized(defaultCompactMode)
    }
  }, [defaultCompactMode, userToggled])

  useEffect(() => {
    const handlePointerMove = (event) => {
      const dragState = dragStateRef.current
      const badgeElement = badgeRef.current

      if (!dragState || !badgeElement) {
        return
      }

      const nextLeft = dragState.startLeft + (event.clientX - dragState.startClientX)
      const nextTop = dragState.startTop + (event.clientY - dragState.startClientY)
      const maxLeft = Math.max(16, window.innerWidth - badgeElement.offsetWidth - 16)
      const maxTop = Math.max(16, window.innerHeight - badgeElement.offsetHeight - 16)
      const clampedLeft = Math.min(Math.max(16, nextLeft), maxLeft)
      const clampedTop = Math.min(Math.max(16, nextTop), maxTop)

      if (
        !dragState.moved &&
        (Math.abs(event.clientX - dragState.startClientX) > 4 ||
          Math.abs(event.clientY - dragState.startClientY) > 4)
      ) {
        dragState.moved = true
        suppressToggleRef.current = true
      }

      setDragPosition({ left: clampedLeft, top: clampedTop })
    }

    const handlePointerUp = () => {
      if (dragStateRef.current?.moved) {
        window.setTimeout(() => {
          suppressToggleRef.current = false
        }, 0)
      }
      dragStateRef.current = null
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [])

  if (!shouldShow) {
    return null
  }

  const badgeTitle = debugMode
    ? 'Debug (mode on)'
    : apiDomainConfig.customApiBaseUrl || apiDomainConfig.customPublicApiBaseUrl
      ? 'Debug (active overrides)'
      : 'Debug (non-default API config)'

  return (
    <div
      ref={badgeRef}
      style={{
        position: 'fixed',
        // Default to the bottom-right, lifted above the floating intercom button, so the badge
        // doesn't sit on top of the left-menu rail (and its "Help & Calls" button) or the FAB.
        // Still fully draggable — once moved it honours the dragged left/top.
        left: dragPosition ? `${dragPosition.left}px` : 'auto',
        right: dragPosition ? 'auto' : '16px',
        top: dragPosition ? `${dragPosition.top}px` : 'auto',
        bottom: dragPosition ? 'auto' : '84px',
        zIndex: 12000,
        maxWidth: minimized ? 'min(220px, calc(100vw - 32px))' : 'min(360px, calc(100vw - 32px))',
        background: '#0f172a',
        color: '#f8fafc',
        borderRadius: '14px',
        padding: minimized ? '8px 10px' : '10px 12px',
        boxShadow: '0 14px 36px rgba(15, 23, 42, 0.35)',
        fontSize: '12px',
        lineHeight: 1.45,
      }}
    >
      <button
        type="button"
        onPointerDown={(event) => {
          if (event.button !== undefined && event.button !== 0) {
            return
          }

          const badgeElement = badgeRef.current
          if (!badgeElement) {
            return
          }

          const badgeRect = badgeElement.getBoundingClientRect()
          dragStateRef.current = {
            startClientX: event.clientX,
            startClientY: event.clientY,
            startLeft: badgeRect.left,
            startTop: badgeRect.top,
            moved: false,
          }
        }}
        onClick={() => {
          if (suppressToggleRef.current) {
            suppressToggleRef.current = false
            return
          }
          setMinimized((previousValue) => !previousValue)
          setUserToggled(true)
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          background: 'transparent',
          color: 'inherit',
          border: 'none',
          padding: 0,
          cursor: dragStateRef.current ? 'grabbing' : 'grab',
          textAlign: 'left',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: '700', marginBottom: minimized ? '0' : '4px' }}>{badgeTitle}</div>
          <div
            style={{
              opacity: 0.82,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '11px',
            }}
          >
            {minimized
              ? `${getDisplayHost(apiDomainConfig.effectiveApiBaseUrl)} • drag or tap`
              : 'Drag header to move • tap to minimize'}
          </div>
        </div>
        <div
          style={{
            flexShrink: 0,
            fontSize: '11px',
            fontWeight: '700',
            color: '#bfdbfe',
            background: 'rgba(59, 130, 246, 0.16)',
            borderRadius: '999px',
            padding: '4px 8px',
          }}
        >
          {minimized ? 'SHOW' : 'HIDE'}
        </div>
      </button>

      {!minimized && (
        <>
          <div style={{ marginTop: '8px', marginBottom: '2px' }}>Version: {appVersion}</div>
          <div style={{ marginBottom: '6px' }}>Build: {lastBuildDate}</div>
          <div style={{ marginBottom: '2px' }}>
            API: {getDisplayHost(apiDomainConfig.effectiveApiBaseUrl)} ({apiDomainConfig.apiSource})
          </div>
          <div style={{ marginBottom: '8px' }}>
            Public: {getDisplayHost(apiDomainConfig.effectivePublicApiBaseUrl)} (
            {apiDomainConfig.publicApiSource})
          </div>
          <div style={{ marginBottom: debugMode ? '8px' : '0', opacity: 0.85 }}>
            Open `?shiva=shakti` for the full API/version console.
          </div>
          {debugMode && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onExitDebugMode?.()
              }}
              style={{
                color: '#fff',
                background: '#dc2626',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 10px',
                cursor: 'pointer',
              }}
            >
              Exit debug mode
            </button>
          )}
        </>
      )}
    </div>
  )
}
